import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AppShell } from './components/AppShell';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Workspace from './pages/Workspace';
import BidSummary from './pages/BidSummary';
import BudgetProjects from './pages/BudgetProjects';
import BudgetDetail from './pages/BudgetDetail';
import Revisions from './pages/Revisions';
import Masters from './pages/Masters';
import Activities from './pages/Activities';
import Users from './pages/Users';
import Settings from './pages/Settings';
import AuditTrail from './pages/AuditTrail';
import Approvals from './pages/Approvals';
import { NoAccess } from './pages/NoAccess';
import './App.css';

function Protected({ children, module }) {
  const { user, can, booting } = useAuth();
  const loc = useLocation();
  if (booting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-booting">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" aria-label="Loading" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (module && !can(module, 'read')) {
    return <AppShell><NoAccess module={module} /></AppShell>;
  }
  return <AppShell>{children}</AppShell>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected module="dashboard"><Dashboard /></Protected>} />
      <Route path="/projects" element={<Protected module="tenders"><Projects /></Protected>} />
      <Route path="/projects/:id" element={<Protected module="tenders"><Workspace /></Protected>} />
      <Route path="/projects/:id/bid-summary" element={<Protected module="tenders"><BidSummary /></Protected>} />
      <Route path="/projects/:id/revisions" element={<Protected module="tenders"><Revisions /></Protected>} />
      <Route path="/budget" element={<Protected module="project"><BudgetProjects view="project" /></Protected>} />
      <Route path="/budget/:id" element={<Protected module="project"><BudgetDetail view="project" /></Protected>} />
      <Route path="/costing" element={<Protected module="costing"><BudgetProjects view="costing" /></Protected>} />
      <Route path="/costing/:id" element={<Protected module="costing"><BudgetDetail view="costing" /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/masters" element={<Protected module="rate_masters"><Masters /></Protected>} />
      <Route path="/activities" element={<Protected module="rate_masters"><Activities /></Protected>} />
      <Route path="/users" element={<Protected module="users"><Users /></Protected>} />
      <Route path="/settings" element={<Protected module="settings"><Settings /></Protected>} />
      <Route path="/audit-trail" element={<Protected module="settings"><AuditTrail /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
